import apiClient from '@/api/axios';
import { Ansicht, Einstellungen, Kunde, KundeUndUnterkunden, TabellenSpalte } from '@/api/model';
import { ApiRouten } from '@/api/routen';
import { i18n } from '@/utils/i18n';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

type EinstellungenMitUploadDateiendungen = Einstellungen & {
    gueltigeUploadDateiendungen?: string[];
};

export const useKundenbereichStore = defineStore('kundenbereich', () => {
    // Typen nicht als nullable markiert, da sie nur während der Initialisierung null sein könnnen und ich nicht bei jeder Verwendung die nullability-Meldung unterdrücken möchte.
    // Gibt sicherlich elegantere Wege, aber so reicht es zunäcsht aus
    const kunde = ref<Kunde>(null!);
    const unterkunden = ref<Kunde[]>(null!);
    const alleKunden = ref<Kunde[]>(null!);
    const einstellungen = ref<Einstellungen>(null!);
    const datenGeladen = ref<boolean>(false);

    // Getters
    const istGueltigeKundennummer = computed(() => (kundennummer: string) => {
        return alleKunden.value?.some((kunde) => kunde.kundennummer?.toLowerCase() === kundennummer.toLowerCase());
    });
    const gueltigeUploadDateiendungen = computed(() => (einstellungen.value as EinstellungenMitUploadDateiendungen | null)?.gueltigeUploadDateiendungen ?? []);

    // Shorthand: Bezeichnung für Eigenschaften 1..5 in aktueller Sprache
    function getEigenschaftenBezeichnung(eigenschaftenIndex: number): string {
        const lang = i18n.global.locale.value;
        const maps = einstellungen.value?.eigenschaftenBezeichnungen!;

        switch (eigenschaftenIndex) {
            case 1:
                return maps.eigenschaften1[lang];
            case 2:
                return maps.eigenschaften2[lang];
            case 3:
                return maps.eigenschaften3[lang];
            case 4:
                return maps.eigenschaften4[lang];
            case 5:
                return maps.eigenschaften5[lang];
            default:
                throw new Error('Es werden nur 5 Eigenschaften unterstützt');
        }
    }

    function getTabellenSpalte(ansicht: Ansicht, name: string): TabellenSpalte {
        return (einstellungen.value?.tabellenLayouts[ansicht].spalten as TabellenSpalte[]).find((s) => s.name === name)!;
    }

    // Actions
    const ladeKundenDaten = async (kundennummer?: string) => {
        const filialenPromise = apiClient.get<KundeUndUnterkunden>(ApiRouten.KUNDENBEREICH.FILIALEN);
        const einstellungenPromise = apiClient.get<Einstellungen>(ApiRouten.KUNDENBEREICH.EINSTELLUNGEN);

        const [filialenResponse, einstellungenResponse] = await Promise.all([filialenPromise, einstellungenPromise]);

        kunde.value = filialenResponse.data.kunde;
        unterkunden.value = filialenResponse.data.unterkunden;
        alleKunden.value = [kunde.value, ...unterkunden.value];

        einstellungen.value = einstellungenResponse.data;

        datenGeladen.value = true;
    };

    const resetKundenDaten = () => {
        kunde.value = null!;
        unterkunden.value = null!;
        alleKunden.value = null!;
        einstellungen.value = null!;
        datenGeladen.value = false!;
    };

    return {
        // State
        kunde,
        unterkunden,
        alleKunden,
        einstellungen,

        datenGeladen,

        // Getters
        istGueltigeKundennummer,
        gueltigeUploadDateiendungen,
        getEigenschaftenBezeichnung,
        getTabellenSpalte,

        // Actions
        ladeKundenDaten,
        resetKundenDaten
    };
});
